import { StateCreator } from 'zustand'
import type { FFEStore } from '../types'
import type { LineItem, Status } from '../../types/ffe'
import { generateNextCode } from '../../utils/code'

export interface LineItemsSlice {
  addLineItem: (
    item: Omit<LineItem, 'id' | 'code'>,
    createAfterId?: string,
    customCode?: string,
    idCallback?: (id: string) => void
  ) => void
  updateLineItem: (id: string, updates: Partial<LineItem>) => void
  removeLineItem: (id: string) => void
  reorderLineItems: (items: LineItem[]) => void
  updateLineItemMaterialStatus: (
    lineItemId: string,
    materialId: string,
    status: Status
  ) => void
  moveLineItemToTopOfGroup: (lineItemId: string) => void
  moveLineItemToBottomOfGroup: (lineItemId: string) => void
  sortLineItemsInGroupBy: (groupId: string, sortKey: 'code' | 'name') => void
  // Loading tracking for async-populated line items
  loadingLineItemIds: string[]
  setLineItemLoading: (id: string, isLoading: boolean) => void
}

export const createLineItemsSlice: StateCreator<
  FFEStore,
  [],
  [],
  LineItemsSlice
> = (set) => ({
  // Loading-state defaults
  loadingLineItemIds: [],
  setLineItemLoading: (id, isLoading) =>
    set((state) => {
      const setWithoutId = state.loadingLineItemIds.filter((x) => x !== id)
      return {
        loadingLineItemIds: isLoading ? [...setWithoutId, id] : setWithoutId
      }
    }),

  addLineItem: (item, createAfterId, customCode, idCallback) =>
    set((state) => {
      const id = crypto.randomUUID()
      const { defaultMargin } = state.document.settings
      const costPrice = item.costPrice || 0
      const sellPrice = item.sellPrice
        ? item.sellPrice
        : costPrice * (1 + defaultMargin / 100)

      // Generate next code based on existing codes
      const existingCodes = state.document.lineItems.map((item) => item.code)
      const nextCode =
        customCode !== undefined ? customCode : generateNextCode(existingCodes)

      // Insert at specific index if provided
      if (createAfterId !== undefined) {
        const createAtIndex =
          state.document.lineItems.findIndex(
            (item) => item.id === createAfterId
          ) + 1

        if (idCallback) {
          idCallback(id)
        }

        return {
          document: {
            ...state.document,
            lineItems: [
              ...state.document.lineItems.slice(0, createAtIndex),
              {
                ...item,
                id: id,
                code: nextCode,
                costPrice,
                sellPrice: Number(sellPrice.toFixed(2))
              },
              ...state.document.lineItems.slice(createAtIndex)
            ]
          }
        }
      }

      if (idCallback) {
        idCallback(id)
      }

      return {
        document: {
          ...state.document,
          lineItems: [
            ...state.document.lineItems,
            {
              ...item,
              id: id,
              code: nextCode,
              costPrice,
              sellPrice: Number(sellPrice.toFixed(2))
            }
          ]
        }
      }
    }),

  updateLineItem: (id, updates) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.map((item) =>
          item.id === id ? { ...item, ...updates } : item
        )
      }
    })),

  removeLineItem: (id) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.filter((item) => item.id !== id)
      },
      loadingLineItemIds: state.loadingLineItemIds.filter((x) => x !== id)
    })),

  reorderLineItems: (items) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: items
      }
    })),
  updateLineItemMaterialStatus: (lineItemId, materialId, status) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.map((item) =>
          item.id === lineItemId
            ? {
                ...item,
                materials: (item.materials || []).map((m) =>
                  m.materialId === materialId ? { ...m, status } : m
                )
              }
            : item
        )
      }
    })),
  moveLineItemToTopOfGroup: (lineItemId) =>
    set((state) => {
      const lineItem = state.document.lineItems.find(
        (item) => item.id === lineItemId
      )
      if (!lineItem) return state

      const filteredItems = state.document.lineItems.filter(
        (item) => item.id !== lineItemId
      )

      const groupIndex = filteredItems.findIndex(
        (item) => item.groupId === lineItem.groupId
      )

      const preceeding = filteredItems.slice(0, groupIndex)
      const succeeding = filteredItems.slice(groupIndex)
      return {
        document: {
          ...state.document,
          lineItems: [...preceeding, lineItem, ...succeeding]
        }
      }
    }),
  moveLineItemToBottomOfGroup: (lineItemId) =>
    set((state) => {
      const lineItem = state.document.lineItems.find(
        (item) => item.id === lineItemId
      )
      if (!lineItem) return state

      const filteredItems = state.document.lineItems.filter(
        (item) => item.id !== lineItemId
      )

      const groupIndex =
        filteredItems
          .map((item) => item.groupId)
          .lastIndexOf(lineItem.groupId) + 1
      const preceeding = filteredItems.slice(0, groupIndex)
      const succeeding = filteredItems.slice(groupIndex)
      return {
        document: {
          ...state.document,
          lineItems: [...preceeding, lineItem, ...succeeding]
        }
      }
    }),
  sortLineItemsInGroupBy: (groupId, sortKey) =>
    set((state) => {
      const groupItems = state.document.lineItems.filter(
        (item) => item.groupId === groupId
      )

      const sortedItems = groupItems.sort((a, b) => {
        if (sortKey === 'code') {
          return a.code.localeCompare(b.code)
        } else {
          return a.name.localeCompare(b.name)
        }
      })

      const otherItems = state.document.lineItems.filter(
        (item) => item.groupId !== groupId
      )

      return {
        document: {
          ...state.document,
          lineItems: [...otherItems, ...sortedItems]
        }
      }
    })
})
